import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, FlaskConical, Loader2, ShieldCheck } from "lucide-react";
import { SRMark } from "../components/SRMark";
import { Button } from "../components/ui/button";
import { DemoInactivityPrompt } from "../components/DemoInactivityPrompt";
import { useAuth } from "../context/AuthContext";
import { API } from "../lib/api";
import {
  clearDemoStorage,
  endLinkDemoSession,
  ensureAccountDemoSession,
  getStoredLinkDemoSession,
  startLinkDemoSession,
  trackAccountDemoEvent,
  trackLinkDemoEvent,
} from "../lib/demoSession";
import Clinicians from "./Clinicians";
import DemoDashboard from "./DemoDashboard";
import DemoReferrals from "./DemoReferrals";

const pagePathFor = (page) => page === "referrals" ? "referral-tokens" : page;

export default function DemoLinkPage({ page = "welcome" }) {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const [validity, setValidity] = useState("loading");
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(() => getStoredLinkDemoSession(accessToken));

  const base = "/demo/access/" + accessToken;
  const nav = useMemo(() => ({
    dashboard: base + "/dashboard",
    clinicians: base + "/clinicians",
    referrals: base + "/referral-tokens",
  }), [base]);

  useEffect(() => {
    let active = true;
    fetch(API + "/demo/access/" + encodeURIComponent(accessToken))
      .then((response) => {
        if (!active) return;
        setValidity(response.ok ? "valid" : "invalid");
      })
      .catch(() => active && setValidity("invalid"));
    return () => { active = false; };
  }, [accessToken]);

  useEffect(() => {
    if (page !== "welcome" && validity === "valid" && !session) navigate(base, { replace: true });
  }, [base, navigate, page, session, validity]);

  useEffect(() => {
    if (page === "welcome" || !session?.session_id || validity !== "valid") return;
    trackLinkDemoEvent(accessToken, session.session_id, "PAGE_VIEW", pagePathFor(page), {});
  }, [accessToken, page, session, validity]);

  const enterDemo = async () => {
    setStarting(true);
    try {
      const created = await startLinkDemoSession(accessToken);
      setSession(created);
      navigate(nav.dashboard);
    } catch {
      setValidity("invalid");
    } finally {
      setStarting(false);
    }
  };

  const scope = session?.session_id ? "link:" + session.session_id : "link:pending";
  const createAccount = async () => {
    if (session?.session_id) {
      await trackLinkDemoEvent(accessToken, session.session_id, "CREATE_ACCOUNT_CLICKED", pagePathFor(page), {});
      await endLinkDemoSession(accessToken, session.session_id, scope);
    }
  };
  const leaveFromPrompt = async () => {
    await createAccount();
    navigate("/signup/gp");
  };
  const trackEvent = (eventType, eventData) => trackLinkDemoEvent(accessToken, session?.session_id, eventType, pagePathFor(page), eventData);

  if (validity === "loading") return <FullPageLoading />;
  if (validity === "invalid") return <UnavailableInvitation />;
  if (page === "welcome") return <InvitationWelcome starting={starting} onEnter={enterDemo} />;
  if (!session) return <FullPageLoading />;

  const shared = {
    firstName: "Dr Alex Morgan",
    nav,
    linkMode: true,
    exitPath: "/signup/gp",
    onExit: createAccount,
    scope,
  };

  return (
    <>
      {page === "dashboard" && <DemoDashboard {...shared} />}
      {page === "clinicians" && (
        <Clinicians
          sampleMode
          demoLinkMode
          demoFirstName="Dr Alex Morgan"
          demoNav={nav}
          demoExitPath="/signup/gp"
          demoOnExit={createAccount}
          demoScope={scope}
          demoTrackEvent={trackEvent}
        />
      )}
      {page === "referrals" && <DemoReferrals {...shared} />}
      <DemoInactivityPrompt linkMode onLeave={leaveFromPrompt} />
    </>
  );
}

export function AuthenticatedDemoPage({ page }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const nav = useMemo(() => ({
    dashboard: "/sample-dashboard",
    clinicians: "/sample-directory",
    referrals: "/sample-referrals",
  }), []);

  useEffect(() => {
    let active = true;
    ensureAccountDemoSession().then((value) => active && setSession(value));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!session?.session_id) return;
    trackAccountDemoEvent(session.session_id, "PAGE_VIEW", pagePathFor(page), {});
  }, [page, session]);

  const scope = "account:" + (session?.session_id || user?.id || "pending");
  const exitDemo = async () => {
    if (session?.session_id) await trackAccountDemoEvent(session.session_id, "SESSION_EXIT", pagePathFor(page), {});
    clearDemoStorage(scope);
  };
  const leaveFromPrompt = async () => {
    await exitDemo();
    navigate("/clinicians");
  };
  const trackEvent = (eventType, eventData) => trackAccountDemoEvent(session?.session_id, eventType, pagePathFor(page), eventData);
  const firstName = user?.user_metadata?.first_name || "Demo GP";
  const shared = { firstName, nav, linkMode: false, exitPath: "/clinicians", onExit: exitDemo, scope };

  return (
    <>
      {page === "dashboard" && <DemoDashboard {...shared} />}
      {page === "clinicians" && (
        <Clinicians
          sampleMode
          demoFirstName={firstName}
          demoNav={nav}
          demoExitPath="/clinicians"
          demoOnExit={exitDemo}
          demoScope={scope}
          demoTrackEvent={trackEvent}
        />
      )}
      {page === "referrals" && <DemoReferrals {...shared} />}
      <DemoInactivityPrompt onLeave={leaveFromPrompt} />
    </>
  );
}

export function PublicDemoPage({ page = "welcome" }) {
  const navigate = useNavigate();
  const nav = useMemo(() => ({
    dashboard: "/demo/dashboard",
    clinicians: "/demo/clinicians",
    referrals: "/demo/referrals",
  }), []);
  const shared = {
    firstName: "Demo GP",
    nav,
    linkMode: true,
    exitPath: "/signup/gp",
    onExit: async () => {},
    scope: "public-demo",
  };
  if (page === "welcome") return <InvitationWelcome starting={false} onEnter={() => navigate(nav.dashboard)} publicDemo />;
  return (
    <>
      {page === "dashboard" && <DemoDashboard {...shared} />}
      {page === "clinicians" && <Clinicians sampleMode demoLinkMode demoFirstName="Demo GP" demoNav={nav} demoExitPath="/signup/gp" demoOnExit={async () => {}} demoScope="public-demo" />}
      {page === "referrals" && <DemoReferrals {...shared} />}
    </>
  );
}

const InvitationWelcome = ({ starting, onEnter, publicDemo = false }) => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-12" data-testid="demo-invitation-welcome">
    <div className="w-full max-w-2xl rounded-2xl border border-blue-100 bg-white p-8 text-center shadow-xl sm:p-12">
      <div className="mx-auto flex w-fit items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
        <SRMark />
        <span className="font-heading text-2xl font-semibold tracking-tight text-slate-900">SimplyRefer</span>
      </div>
      <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-primary"><FlaskConical className="h-7 w-7" /></div>
      <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-primary">{publicDemo ? "Public demonstration" : "Private demonstration access"}</p>
      <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{publicDemo ? "Try SimplyRefer safely" : "Welcome to SimplyRefer's special demo invitation"}</h1>
      <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600">Explore a fictional GP dashboard, search diverse sample clinicians and try all three referral options. No account is created and nothing is sent to a clinician.</p>
      <Button size="lg" className="mt-8 min-w-52" onClick={onEnter} disabled={starting} data-testid="enter-demo-button">
        {starting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
        Enter demo
      </Button>
      <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Fictional information only · No patient information required · No production analytics</p>
    </div>
  </div>
);

const FullPageLoading = () => (
  
  <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
);

const UnavailableInvitation = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
    <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <SRMark />
      <h1 className="mt-5 font-heading text-2xl font-semibold text-slate-900">This demo invitation is no longer available</h1>
      <p className="mt-2 text-sm text-slate-600">The link may have expired or been revoked. You can still create a free GP account to explore SimplyRefer.</p>
      <Button className="mt-6" onClick={() => { window.location.href = "/signup/gp"; }}>Create Free GP Account</Button>
    </div>
  </div>
);
